
import Mpegts from "mpegts.js";
import { MediaMTXWebRTCReader } from './mtxReader.ts';
import Hls from 'hls.js';

const CONNECT_TIMEOUT_MS = 30000;
const RECONNECT_DELAY_MS = 10000;

type CameraStatus = "connecting" | "connected" | "disconnected";

const cameraStatusMap: Record<string, CameraStatus> = {};

export const getCameraStatus = (src: string): CameraStatus => {
    return cameraStatusMap[src] ?? "disconnected";
};

export interface VideoConnection {
    player?: Mpegts.Player | MediaMTXWebRTCReader;
    video: HTMLVideoElement;
    checkInterval: ReturnType<typeof setInterval> | null;
    lastFrameArrived: number;
    destroy: (force?: boolean) => void;
    hasReceivedFrame: boolean;
}

const connectionsCache: Record<string, VideoConnection> = {};

const newPlayer = async (url: string, video: HTMLVideoElement, isLive = false,
    onError: () => void,
    onTimeUpdate: () => void,
    onLoaded: () => void,
    deviceId?: string,
) => {
    try {
        Mpegts.LoggingControl.enableWarn = false;

        let player: Mpegts.Player | MediaMTXWebRTCReader | Hls | undefined;

        if (isLive) {
            player = new MediaMTXWebRTCReader({
                url,
                onError(e: unknown) {
                    console.error('[MediaMTX] error:', e);
                },
                onTrack(e: RTCTrackEvent) {
                    video.srcObject = e.streams[0];
                    //Live stream is attached → hide spinner
                    onLoaded();
                }
            });

            video.playsInline = true;
            video.autoplay = true;
            video.addEventListener('canplay', onLoaded);
            video.addEventListener('ended', onError);
        } else {
            if (Hls.isSupported()) {
                // const hls = new Hls({
                //     maxBufferLength: 6,         // Try to keep 6s of video in buffer (~1 segments)
                //     maxMaxBufferLength: 12,      // Never buffer more than 12s (~2 segments)
                //     maxBufferSize: 6 * 1024 * 1024,
                //     startFragPrefetch: true,
                //     autoStartLoad: false,
                // });
                const hls = new Hls({
                    maxBufferLength: 6,
                    maxMaxBufferLength: 12,
                    maxBufferSize: 6 * 1024 * 1024,
                    startFragPrefetch: true,
                    autoStartLoad: false,

                    // ⬇️ iOS INLINE SAFE SETTINGS
                    enableWorker: true,
                    lowLatencyMode: false,
                    backBufferLength: 0,
                });
                hls.attachMedia(video);
                hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                    hls.loadSource(url);
                });
                player = hls;
            } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                video.src = url;
                video.addEventListener('loadedmetadata', () => {
                    // video.play().catch(console.error);
                    video.play().catch(err => {
                        if (
                            err?.message?.includes("The play() request was interrupted") ||
                            err?.name === "AbortError"
                        ) {
                            console.warn("[Suppressed play() interruption]:", err.message);
                        } else {
                            console.error("[VideoJS] video.play() failed:", err);
                        }
                    });
                    onLoaded();
                });
            } else {
                console.warn('zxz [newPlayer] HLS not supported in this browser');
                onError();
            }
        }

        video.addEventListener('error', onError);
        video.addEventListener('timeupdate', onTimeUpdate);

        return player;
    } catch (err) {
        console.error('[newPlayer] error:', err);
        onError();
        return;
    }
}

export const destroyAllConnections = () => {
    Object.keys(connectionsCache).forEach((key) => {
        connectionsCache[key].destroy(true);
        delete connectionsCache[key];
    });
};

export const destroyLowStreamForCamera = (
    remoteUrl: string,
    cameraId: string
) => {
    const lowUrl = `${remoteUrl}/${cameraId}/whep`;

    const connection = connectionsCache[lowUrl];
    if (connection) {
        console.log("[VideoConnection] Destroy LOW stream:", lowUrl);
        connection.destroy(true);
        delete connectionsCache[lowUrl];
    }
};

export const restoreLowStreamForCamera = (
    remoteUrl: string,
    cameraId: string
) => {
    const lowUrl = `${remoteUrl}/${cameraId}/whep`;

    if (!connectionsCache[lowUrl]) {
        console.log("[VideoConnection] Restore LOW stream:", lowUrl);
        createVideoConnection(lowUrl, cameraId);
    }
};

export const createVideoConnection = (
    url: string,
    cameraId?: string,
    isLastPlayback?: boolean,
    isHighStream?: boolean,
): VideoConnection => {
    // const isLive = isRelayUrl(url);
    if (!url) {
        throw new Error("[createVideoConnection] url is undefined or empty");
    }
    const isLive = url.includes('/whep');
    const isHigh = url.includes('_high');
    const connection = connectionsCache[url];
    if (connection) {
        return connection;
    }

    cameraStatusMap[url] = "connecting";

    const CONNECTING_RETRY_MS = 20000; // 8 seconds
    let startedAt = Date.now();
    let retryCount = 0;
    const hasRetryLimit = !isHigh;
    const MAX_RETRIES = 10;
    let permanentDisconnect = false;
    let reconnecting = false;

    const video = document.createElement('video');
    video.setAttribute("data-testid", Date.now().toString());
    video.muted = true;

    const markConnectedOnFirstFrame = () => {
        if (
            !result.hasReceivedFrame &&
            video.videoWidth > 0 &&
            video.videoHeight > 0
        ) {
            reconnecting = false; //  unlock stall detection
            result.hasReceivedFrame = true;
            retryCount = 0;          //  reset retries
            cameraStatusMap[url] = "connected";
          //  console.log("FIRST FRAME", `${cameraId}`, url);
        }
    };

    const onTimeUpdate = () => {
        result.lastFrameArrived = Date.now();
        markConnectedOnFirstFrame();
    };

    const onCheck = async (msg: string) => {
        const now = Date.now();

        // --- CASE 1: Initial Connection (No frame received yet) ---
        if (!result.hasReceivedFrame) {
            const elapsed = now - startedAt;

            if (elapsed > CONNECTING_RETRY_MS) {
                if (hasRetryLimit) {
                    // --- LOW STREAM RETRY LOGIC ---
                    if (retryCount < MAX_RETRIES) {
                        retryCount += 1;
                        startedAt = now;
                       // console.warn(`[VC][${cameraId}] Low Stream: Retry #${retryCount}`);

                        try {
                            if (result.player) result.player.destroy?.();
                            result.player = await newPlayer(
                                url,
                                result.video,
                                isLive,
                                onEnded,
                                onTimeUpdate,
                                onLoaded
                            );
                        } catch (e) {
                            console.error("[VC] Low Stream Retry failed", e);
                        }
                    } else {
                        // TERMINAL DISCONNECT: Stop for Low Streams
                        permanentDisconnect = true;
                        cameraStatusMap[url] = "disconnected";
                        console.error(`[VC][${cameraId}] MAX RETRIES REACHED → STOPPING`);

                        if (result.checkInterval) {
                            clearInterval(result.checkInterval);
                            result.checkInterval = null;
                        }
                    }
                } 
                else {
                    // --- HIGH STREAM / PLAYBACK LOGIC: Retry Forever ---
                    startedAt = now;
                    onEnded("high-stream-reconnect");
                }
            }
            return;
        }

        // --- CASE 2: Stream Stall (Connected but stopped receiving frames) ---
        const diff = now - result.lastFrameArrived;
        if (diff > CONNECT_TIMEOUT_MS && !reconnecting) {
            reconnecting = true;
            if (hasRetryLimit) {
                console.warn(`[VC][${cameraId}] Low Stream Stalled → Attempting Reconnect`);
            }
            onEnded("frame-timeout");
        }
    };

    const onLoaded = () => {
        console.log('[VideoConnection] metadata arrived');
        // setCameraOnlineStatus({ url, status: DeviceStatuses.online });
    };

    const onEnded = async (e?: unknown, ...args: unknown[]) => {
        //  setCameraOnlineStatus({ url, status: DeviceStatuses.offline });
        // if (hasRetryLimit) {
        //     if (permanentDisconnect) return;

        //     cameraStatusMap[url] = "connecting";
        //     result.hasReceivedFrame = false;
        //     return;
        // }
        if (hasRetryLimit && permanentDisconnect) {
            return;
        }

        const isError = (e as Event)?.type === 'error' || e === 'MediaError';
        const destroyNotNeeded = isLastPlayback && !isError;

        if (!destroyNotNeeded) {
            result.destroy(true); // for HLS only if needed
        }
        if (isLive) {
            result.destroy(true);
            if (hasRetryLimit && permanentDisconnect) return;

            await new Promise(r => setTimeout(r, RECONNECT_DELAY_MS));
            result.player = await newPlayer(url, result.video, isLive, onEnded, onTimeUpdate, onLoaded);
            if (result.checkInterval) clearInterval(result.checkInterval);
            result.checkInterval = setInterval(onCheck, CONNECT_TIMEOUT_MS, '[onEnded]');

            result.hasReceivedFrame = false;
            reconnecting = false;
            startedAt = Date.now();
            cameraStatusMap[url] = "connecting";
        }
    }

    const result: VideoConnection = {
        player: undefined,
        video,
        lastFrameArrived: 0,
        checkInterval: null,
        destroy: (force = false) => {
            try {
                if (result.checkInterval) {
                    clearInterval(result.checkInterval);
                }
                if (!isLive || force || hasRetryLimit) {

                    try {
                        if (!result.player) {
                            return;
                        }
                        delete connectionsCache[url];
                        if (result.player) {
                            result.player.destroy(true);
                        }
                    } catch (err) {
                        console.error('[videoConnection] error:', err);
                    } finally {
                        result.player = undefined;
                    }
                }

                result.video.pause();          // stops playback
                result.video.src = '';         // clears current source
                result.video.load();           // resets state and event lifecycle
                result.video.removeEventListener('ended', onEnded);
                result.video.removeEventListener('error', onEnded);
                result.video.removeEventListener('timeupdate', onTimeUpdate);
            } catch { }
        },
    };

    newPlayer(url, video, isLive, onEnded, onTimeUpdate, onLoaded, cameraId).then(p => {
        if (p) {
            result.player = p;
        } else {
            console.error('newPlayer] player not created')
        }
    });
    if (isLive) {
        result.checkInterval = setInterval(onCheck, CONNECT_TIMEOUT_MS, '[createVideoConnection]');
    }

    if (isLive) {
        connectionsCache[url] = result;
    }

    return result;
}
